import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { RecurrenceStateService } from 'src/app/services/billing/recurrence-state.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ExperimentProps } from 'src/app/services/growthbook/types';
import { UserCountStateService } from 'src/app/services/billing/user-count-state.service';

@Component({
  selector: 'app-payment-recurrence-tabs',
  templateUrl: './payment-recurrence-tabs.component.html',
  styleUrls: ['./payment-recurrence-tabs.component.scss'],
})
export class PaymentRecurrenceTabsComponent implements OnInit, OnDestroy {
  @Input() payRecurrence: PricePeriodicity = 'month';
  // Allow consumers to explicitly hide the calculator (e.g., in plan comparison)
  @Input() hideCalculator = false;
  // Calculator visibility is now determined internally via GrowthBook
  showCalculator = false;
  @Output() recurrenceChange = new EventEmitter<PricePeriodicity>();
  @Output() userCountChange = new EventEmitter<number | null>();

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly recurrenceStateService: RecurrenceStateService,
    private readonly growthBookService: GrowthBookService,
    private readonly userCountState: UserCountStateService,
  ) {}

  ngOnInit(): void {
    // Determine if the calculator should be shown based on GrowthBook experiment
    const exp = this.growthBookService.getFeatureValue<ExperimentProps>('billing-calculator-experiment');
    this.showCalculator = exp?.variationid === 'B';
    // Subscribe to shared state changes
    this.recurrenceStateService.payRecurrence$
      .pipe(takeUntil(this.destroy$))
      .subscribe(recurrence => {
        if (this.payRecurrence !== recurrence) {
          this.payRecurrence = recurrence;
          this.recurrenceChange.emit(recurrence);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRecurrenceClick(recurrence: PricePeriodicity): void {
    // Update the shared state service
    this.recurrenceStateService.setPayRecurrence(recurrence);

    // Also emit locally for backward compatibility
    this.recurrenceChange.emit(recurrence);
  }

  onUsersChange(count: number | null) {
    // Emit for local consumers
    this.userCountChange.emit(count);
    // Update shared state so other components (e.g., plan comparison) can react
    this.userCountState.setUserCount(count);
  }
}
